import { useEffect, useRef, useState } from 'react';
import type { EvidenceBundle, GameRoundId, RoundSnapshot } from '@urbanrats/contracts';
import { countCrossings, fromUtcInstant, lightningCommitment, lightningPool, verifyLightning, type CountRules, type CrossingDecision } from '@urbanrats/domain';
import type { ClientGateways } from '../gateway/index.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { fmtOdds } from '../lib/format.ts';
import { VideoStage } from './VideoStage.tsx';
import type { LightningPolicy } from '@urbanrats/contracts';

/**
 * Round evidence + replay. Replay uses its own <video> element (never the live player, whose timeline
 * the running round is bound to) and seeks to the media time of each counted crossing.
 */
export function EvidencePanel({ gw, gameRoundId }: { gw: ClientGateways; gameRoundId: string }) {
  const { t, intl } = useI18n();
  const [data, setData] = useState<{ ev: EvidenceBundle | null; round: RoundSnapshot | null; playback: string | null; replay: { url: string; ptsOffsetMicros: number } | null } | null>(null);
  const [replayError, setReplayError] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    let live = true;
    const id = gameRoundId as GameRoundId;
    void Promise.all([gw.evidence.getEvidence(id), gw.game.getRound(id)]).then(async ([ev, round]) => {
      let playback: string | null = null;
      let replay: { url: string; ptsOffsetMicros: number } | null = null;
      if (ev?.proof && ev.proof.manifest.sourceMode !== 'SIMULATION' && gw.media.getReplay) {
        // Backend rounds: signed, short-lived link to the redacted rendition of exactly this window.
        const r = await gw.media.getReplay(id).catch(() => null);
        replay = r ? { url: r.playlistUrl, ptsOffsetMicros: r.ptsOffsetMicros } : null;
      } else if (ev?.mediaRef) {
        const views = await gw.media.listSources().catch(() => []);
        const v = views.find((x) => x.sourceId === ev.mediaRef!.sourceId);
        playback = v?.playbackKind === 'file' ? v.playbackPath : null;
      }
      if (live) setData({ ev, round, playback, replay });
    });
    return () => {
      live = false;
    };
  }, [gw, gameRoundId]);

  if (!data) return <p className="muted">…</p>;
  const { ev, round, playback, replay } = data;
  if (round?.state === 'VOIDED' && !ev)
    return (
      <p className="warn-box" data-testid="evidence">
        {t('evidence.voided')}
      </p>
    );
  if (!ev || !round) return <p className="muted">{t('evidence.unavailable')}</p>;

  const m = round.manifest;
  const rules: CountRules = {
    gameRoundId: m.gameRoundId,
    lineId: m.line.lineId,
    countedDirection: m.line.countedDirection,
    includedClasses: m.includedClasses,
    window:
      m.captureWindowPts && m.sourceEpoch
        ? { kind: 'MEDIA_PTS', sourceEpoch: m.sourceEpoch, startMicros: m.captureWindowPts.startMicros, endMicros: m.captureWindowPts.endMicros }
        : { kind: 'CAPTURE_TIME', startMs: fromUtcInstant(m.timing.captureStartAt), endMs: fromUtcInstant(m.timing.captureEndAt) },
  };
  // Decisions are recomputed locally for display only; the published count is the server's.
  // Games that count both directions decide each event in its own direction.
  const both = m.rules.kind === 'DUEL' || ('directions' in m.rules && m.rules.directions === 'ANY');
  const a = countCrossings(ev.events, rules).decisions;
  const b = both ? countCrossings(ev.events, { ...rules, countedDirection: rules.countedDirection === 'A_TO_B' ? 'B_TO_A' : 'A_TO_B' }).decisions : null;
  const decisions: Map<string, CrossingDecision> = new Map([...a].map(([id, d]): [string, CrossingDecision] => [id, d === 'WRONG_DIRECTION' && b ? (b.get(id) ?? d) : d]));
  const yesNo = (b: boolean) => (b ? t('evidence.yes') : t('evidence.no'));
  const startMs = fromUtcInstant(m.timing.captureStartAt);
  const replayEpoch = ev.proof?.segments[0]?.sourceEpoch ?? null;
  // Seconds into the playable media: backend replay maps epoch PTS onto the replay timeline.
  const eventSeconds = (e: EvidenceBundle['events'][number]) =>
    replay
      ? e.framePts.sourceEpoch === replayEpoch
        ? (e.framePts.ptsMicros - replay.ptsOffsetMicros) / 1e6
        : null
      : m.captureWindowPts
        ? e.framePts.ptsMicros / 1e6
        : e.capturedAt
          ? (fromUtcInstant(e.capturedAt) - startMs) / 1000
          : null;
  const canJump = !!playback || !!replay;
  const jump = (s: number) => {
    const v = video.current;
    if (!v) return;
    v.currentTime = Math.max(0, s - 1.5);
    void v.play().catch(() => undefined);
  };

  return (
    <div className="evidence" data-testid="evidence">
      {ev.status === 'SIMULATION_ONLY' && <p className="warn-box">{t('evidence.simNote')}</p>}
      {m.sourceMode === 'RECORDED_DEMO' && <p className="warn-box">{t('evidence.recordedNote')}</p>}
      {ev.roundState !== 'SETTLED' && round.state !== 'SETTLED' && <p className="note">{t('evidence.reason', { r: round.state })}</p>}
      <dl className="kv">
        <dt>{t('history.col.round')}</dt>
        <dd>{ev.gameRoundId}</dd>
        <dt>{t('evidence.status')}</dt>
        <dd>{ev.status}</dd>
        <dt>{t('history.col.count')}</dt>
        <dd data-testid="evidence-count">{ev.count}</dd>
        <dt>{t('evidence.verified')}</dt>
        <dd>{yesNo(ev.evidenceVerified)}</dd>
        <dt>{t('evidence.confirmed')}</dt>
        <dd>{yesNo(ev.countIndependentlyConfirmed)}</dd>
        <dt>Model</dt>
        <dd className="mono">{m.modelVersion}</dd>
        <dt>{t('evidence.manifest')}</dt>
        <dd className="mono">{ev.manifestHash}</dd>
        <dt>{t('evidence.config')}</dt>
        <dd className="mono">{ev.configHash}</dd>
      </dl>
      <LightningCheck round={round} ev={ev} policy={gw.promo?.lightningPolicy ?? null} />
      <p className="note">{t('evidence.bettingClosed', { t: new Date(fromUtcInstant(m.timing.bettingCloseAt)).toLocaleTimeString(intl, { hour12: false }) })}</p>
      {m.captureWindowPts && (
        <p className="note">
          {t('evidence.window', { a: (m.captureWindowPts.startMicros / 1e6).toFixed(2), b: (m.captureWindowPts.endMicros / 1e6).toFixed(2), epoch: m.sourceEpoch ?? '' })}
        </p>
      )}

      {ev.proof && <ProofSection ev={ev} />}

      {replay && (
        <>
          <h3>{t('evidence.replay')}</h3>
          <p className="note">{t('evidence.redactedNote')}</p>
          {replayError && <p className="field-error">{t('evidence.replayExpired')}</p>}
          <div className="replay" data-testid="replay">
            <VideoStage
              geometry={m}
              sourceMode={m.sourceMode}
              testPattern={null}
              pulse={0}
              reducedMotion={false}
              lineLocked
              media={{
                node: <HlsReplay url={replay.url} videoRef={video} onSize={setSize} onError={() => setReplayError(true)} />,
                intrinsic: size,
              }}
            />
          </div>
        </>
      )}

      {playback && (
        <>
          <h3>{t('evidence.replay')}</h3>
          <div className="replay" data-testid="replay">
            <VideoStage
              geometry={m}
              sourceMode={m.sourceMode}
              testPattern={null}
              pulse={0}
              reducedMotion={false}
              lineLocked
              media={{
                node: (
                  <video
                    ref={video}
                    className="media"
                    src={playback}
                    crossOrigin="anonymous"
                    muted
                    playsInline
                    controls
                    preload="metadata"
                    data-testid="replay-video"
                    onLoadedMetadata={(e) => setSize({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight })}
                  />
                ),
                intrinsic: size,
              }}
            />
          </div>
        </>
      )}

      <h3>{t('evidence.events')}</h3>
      {(['counted', 'excluded'] as const).map((group) => {
        const list = ev.events
          .filter((e) => (decisions.get(e.eventId) === 'COUNTED') === (group === 'counted'))
          .sort((x, y) => (x.capturedAt ?? '').localeCompare(y.capturedAt ?? '') || x.framePts.ptsMicros - y.framePts.ptsMicros);
        const items = (
          <ol className="events" data-testid={`events-${group}`}>
            {list.map((e) => {
          const d = decisions.get(e.eventId);
          const s = eventSeconds(e);
          const rel = replay ? (e.capturedAt ? (fromUtcInstant(e.capturedAt) - startMs) / 1000 : null) : m.captureWindowPts && s !== null ? s - m.captureWindowPts.startMicros / 1e6 : s;
          const label = rel === null ? '?' : `${rel < 0 ? '−' : '+'}${Math.abs(rel).toFixed(1)}`;
          return (
            <li key={e.eventId} className={d === 'COUNTED' ? 'counted' : 'excluded'}>
              <span className="tabular">{label}s</span> <span>{e.vehicleClass}</span> <span>{e.direction}</span>{' '}
              <span className="tag">{d === 'COUNTED' ? `✓ ${t('evidence.counted')}` : `✕ ${t('evidence.notCounted')} · ${d}`}</span>
              {e.uncertain && <span className="tag"> ⚠ {e.evidenceRef.split(';')[1] ?? 'uncertain'}</span>}
              {canJump && s !== null && (
                <button type="button" className="link-btn" onClick={() => jump(s)} data-testid="replay-jump">
                  {t('evidence.jump', { t: s.toFixed(1) })}
                </button>
              )}
            </li>
          );
            })}
          </ol>
        );
        return group === 'counted' ? (
          <div key={group}>
            <p className="note">{t('evidence.countedN', { n: list.length })}</p>
            {items}
          </div>
        ) : (
          list.length > 0 && (
            <details key={group}>
              <summary>{t('evidence.excludedN', { n: list.length })}</summary>
              {items}
            </details>
          )
        );
      })}
    </div>
  );
}

/** Plays the signed HLS replay with hls.js (Chromium has no native HLS). */
function HlsReplay({ url, videoRef, onSize, onError }: { url: string; videoRef: React.RefObject<HTMLVideoElement | null>; onSize: (s: { w: number; h: number }) => void; onError: () => void }) {
  const errRef = useRef(onError);
  useEffect(() => {
    errRef.current = onError;
  }, [onError]);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let hls: import('hls.js').default | null = null;
    let dead = false;
    void import('hls.js').then(({ default: Hls }) => {
      if (dead) return;
      if (!Hls.isSupported()) {
        v.src = url;
        return;
      }
      hls = new Hls({ manifestLoadingMaxRetry: 1, fragLoadingMaxRetry: 1 });
      hls.on(Hls.Events.ERROR, (_e, d) => {
        if (d.fatal) errRef.current();
      });
      hls.loadSource(url);
      hls.attachMedia(v);
    });
    return () => {
      dead = true;
      hls?.destroy();
    };
  }, [url, videoRef]);
  return (
    <video
      ref={videoRef}
      className="media"
      muted
      playsInline
      controls
      preload="metadata"
      data-testid="replay-video"
      onLoadedMetadata={(e) => onSize({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight })}
    />
  );
}

/** Signatures, witness receipts and the segment chain, as recorded (the verifier CLI re-checks them). */
function ProofSection({ ev }: { ev: EvidenceBundle }) {
  const { t, intl } = useI18n();
  const p = ev.proof!;
  const when = (iso: string | undefined) => (iso ? new Date(iso).toLocaleTimeString(intl, { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3, hour12: false }) : '—');
  const problems = [...p.resultRecord.evidence.problems, ...p.resultRecord.evidence.alarms];
  return (
    <section className="rw-box" data-testid="evidence-proof">
      <h3>{t('evidence.proof')}</h3>
      {!p.witnessIndependent && <p className="warn-box">{t('evidence.witnessNotIndependent')}</p>}
      <dl className="kv">
        <dt>{t('evidence.manifestWitness')}</dt>
        <dd>
          {when(p.manifestWitness?.receivedAt)} · <span className="mono">{p.manifestSignature.keyId}</span>
        </dd>
        <dt>{t('evidence.resultWitness')}</dt>
        <dd>
          {when(p.resultWitness?.receivedAt)} · <span className="mono">{p.resultSignature.keyId}</span>
        </dd>
        <dt>{t('evidence.segments')}</dt>
        <dd data-testid="evidence-segments">
          {p.segments.length} · <span className="mono">{p.resultRecord.evidence.chainHead?.slice(0, 16) ?? '—'}</span>
        </dd>
      </dl>
      {problems.length > 0 && (
        <details open>
          <summary>{t('evidence.problems')}</summary>
          <ul className="events" data-testid="evidence-problems">
            {problems.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </details>
      )}
      <p className="note">{t('evidence.hashNote')}</p>
    </section>
  );
}

/**
 * Independent check in the browser: sha256(seed) = commitment, and the seed reproduces the boosts that
 * were shown when betting opened. Uses the round's own locked policy version.
 */
function LightningCheck({ round, ev, policy: published }: { round: RoundSnapshot; ev: EvidenceBundle; policy: LightningPolicy | null }) {
  const { t, intl } = useI18n();
  const offer = round.manifest.lightning;
  if (!offer) return <p className="note">{t('evidence.lightningNone')}</p>;
  const policy = published?.policyVersion === offer.policyVersion ? published : null;
  const reveal = ev.lightningReveal;
  const ok = !!reveal && !!policy && verifyLightning(offer, reveal.serverSeedHex, round.gameRoundId, policy, lightningPool(round.manifest.odds, policy));
  // Without the published policy the draw cannot be replayed, but the commitment still can be checked.
  const commitOnly = !!reveal && !policy && lightningCommitment(reveal.serverSeedHex) === offer.commitment;
  return (
    <section className="rw-box" data-testid="evidence-lightning">
      <h3>⚡ {t('evidence.lightning')}</h3>
      <ul className="events">
        {offer.boosts.map((b) => (
          <li key={b.selection}>
            {t(`sel.${b.selection}` as MessageKey)} · {t(`tier.${b.tier}` as MessageKey)} {fmtOdds(b.factor, intl)}
          </li>
        ))}
      </ul>
      <dl className="kv">
        <dt>{t('evidence.lightningCommit')}</dt>
        <dd className="mono">{offer.commitment}</dd>
        <dt>{t('evidence.lightningSeed')}</dt>
        <dd className="mono">{reveal?.serverSeedHex ?? '—'}</dd>
      </dl>
      <p role="status" className={ok || commitOnly ? 'ok-text' : 'field-error'} data-testid="lightning-verify">
        {ok ? t('evidence.lightningOk') : commitOnly ? t('evidence.lightningCommitOk') : t('evidence.lightningBad')}
      </p>
    </section>
  );
}
